import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageSquare, Mail, StickyNote, Users, Building, Pencil, UserCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ScoreChip } from "../components/ScoreChip";
import { PhoneLink, EmailLink } from "../components/ContactLinks";
import LeadEngagement from "../components/LeadEngagement";
import LeadEdit from "../components/LeadEdit";

const KINDS = [
  { k: "call", label: "Call", icon: Phone },
  { k: "text", label: "Text", icon: MessageSquare },
  { k: "email", label: "Email", icon: Mail },
  { k: "note", label: "Note", icon: StickyNote },
  { k: "meeting", label: "Meeting", icon: Users },
];

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [score, setScore] = useState(null);
  const [activities, setActivities] = useState([]);
  const [kind, setKind] = useState("call");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [funnel, setFunnel] = useState(null); // { client_id, tx_count } | null
  const [converting, setConverting] = useState(false);

  const load = async () => {
    const [l, s, a, f] = await Promise.all([
      api.get(`/leads/${id}`),
      api.get(`/leads/${id}/score`),
      api.get(`/leads/${id}/activities`),
      api.get(`/leads/funnel-summary`),
    ]);
    setLead(l.data); setScore(s.data); setActivities(a.data);
    setFunnel(f.data.leads?.[id] || null);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const convertToClient = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      if (data.already_converted) {
        toast.success("Fresh portal invite sent — client already on file");
      } else {
        toast.success(data.emailed ? "Client created · portal invite emailed" : "Client created · email pending");
      }
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "disclosure_incomplete") {
        toast.error(detail.message, {
          duration: 8000,
          action: {
            label: "Open Settings",
            onClick: () => window.location.assign("/settings#brokerage-licensing"),
          },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Convert failed");
      }
    } finally { setConverting(false); }
  };

  const logActivity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/leads/${id}/activities`, { kind, body });
      setBody("");
      toast.success("Activity logged");
      await load();
    } catch { toast.error("Failed to log activity"); }
    finally { setSaving(false); }
  };

  if (!lead) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div>
      <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-leads-detail">
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tighter text-slate-900" data-testid="lead-detail-name">{lead.full_name}</h1>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">{lead.stage.replace("_"," ")} · {lead.lead_type}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {score && <ScoreChip temperature={score.temperature} score={score.score} />}
                <button onClick={() => setEditing(true)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" aria-label="Edit lead" data-testid="lead-edit-btn">
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Funnel stage — one-tap promote to Client */}
            {funnel === null ? (
              <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="funnel-promote-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-widest text-emerald-800">Next step</div>
                    <p className="mt-1 text-sm text-emerald-900">Promote to client — auto-emails a private portal invite so you can share listings.</p>
                  </div>
                  <button
                    onClick={convertToClient}
                    disabled={converting || !lead.email}
                    title={!lead.email ? "Add an email first" : undefined}
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 shrink-0"
                    style={{ background: "var(--primary-hex)" }}
                    data-testid="lead-convert-btn"
                  >
                    <UserCheck className="h-3.5 w-3.5" />
                    {converting ? "Converting…" : "Convert to Client"}
                  </button>
                </div>
                {!lead.email && (
                  <p className="mt-2 text-xs text-emerald-800">Add an email address to enable the portal invite.</p>
                )}
              </div>
            ) : (
              <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="funnel-client-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">Funnel stage</div>
                    <p className="mt-1 text-sm font-semibold text-slate-900">Client on file</p>
                    <p className="text-xs text-slate-500 mt-0.5">{funnel.tx_count} transaction{funnel.tx_count === 1 ? "" : "s"} on this client</p>
                  </div>
                  <Link
                    to="/clients"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 shrink-0"
                    data-testid="lead-view-client-link"
                  >
                    View client <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
                <button
                  onClick={convertToClient}
                  disabled={converting}
                  className="mt-2 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                  data-testid="lead-reinvite-btn"
                >
                  {converting ? "Sending…" : "Resend portal invite"}
                </button>
              </div>
            )}

            <dl className="mt-6 space-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">Phone</dt>
                <dd className="text-slate-900 mt-1" data-testid="lead-detail-phone">
                  {lead.phone
                    ? <PhoneLink phone={lead.phone} testId="lead-detail-phone-link" />
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">Email</dt>
                <dd className="text-slate-900 mt-1 break-all" data-testid="lead-detail-email">
                  {lead.email
                    ? <EmailLink email={lead.email} testId="lead-detail-email-link" />
                    : "—"}
                </dd>
              </div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Source</dt><dd className="text-slate-900">{lead.source || "—"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Budget</dt><dd className="text-slate-900 tabular-nums">{lead.budget_min ? `$${Math.round(lead.budget_min/1000)}k` : "—"} – {lead.budget_max ? `$${Math.round(lead.budget_max/1000)}k` : "—"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Areas</dt><dd className="text-slate-900">{lead.target_areas || "—"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Pre-approved</dt><dd className="text-slate-900">{lead.pre_approved ? "Yes" : "No"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Last contact</dt><dd className="text-slate-900">{lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleString() : "Never"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Consent</dt><dd className="text-slate-900">{lead.do_not_contact ? <span className="text-red-700 font-semibold">Do Not Contact</span> : `${lead.sms_consent ? "SMS ":""}${lead.email_consent ? "Email":""}${!lead.sms_consent && !lead.email_consent ? "None on file" : ""}`}</dd></div>
            </dl>
            {lead.notes && <p className="mt-6 text-sm text-slate-700 border-t border-slate-100 pt-4 whitespace-pre-wrap">{lead.notes}</p>}
          </div>

          {score?.suggested_action && (
            <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="lead-suggested-next">
              <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 mb-2">Suggested next</div>
              <p className="text-sm text-slate-800">{score.suggested_action}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Log activity</h2>
            <p className="text-xs text-slate-500 mt-1">Outbound kinds (call, text, email, meeting, note) update the last-contact clock. Engagement kinds do not.</p>
            <form onSubmit={logActivity} className="mt-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {KINDS.map(({ k, label, icon: Icon }) => (
                  <button key={k} type="button" onClick={() => setKind(k)} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`log-kind-${k}`}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={2} placeholder="Optional note about the touch…" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="log-body-input" />
              <button type="submit" disabled={saving} className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "var(--primary-hex)" }} data-testid="log-submit-btn">
                {saving ? "Logging…" : "Log activity"}
              </button>
            </form>
          </div>

          <LeadEngagement leadId={id} />

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Activity timeline</h2>
            </div>
            <ul>
              {activities.length === 0 && <li className="px-6 py-8 text-sm text-slate-500" data-testid="activity-empty">No activity yet.</li>}
              {activities.map(a => (
                <li key={a.id} className="px-6 py-4 border-b border-slate-100 last:border-b-0" data-testid={`activity-${a.id}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="min-w-0">
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-2">{a.kind.replace("_"," ")}</span>
                      {a.body && <span className="text-sm text-slate-800">{a.body}</span>}
                    </div>
                    <time className="text-xs text-slate-500 shrink-0">{new Date(a.occurred_at).toLocaleString()}</time>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {editing && <LeadEdit lead={lead} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}
